<template>
  <div class="snake-game-container" tabindex="0" @keydown="handleKeydown" ref="gameContainer">
    <div class="game-overlay" v-if="!isPlaying && !gameOver">
      <div class="start-screen">
        <h1>Mission Snake</h1>
        <p>Navigate the snake to collect the requested items!</p>
        <button class="start-btn" @click="startGame">Start Game</button>
      </div>
    </div>

    <div class="game-overlay" v-if="gameOver">
      <div class="game-over-screen">
        <h1>Game Over</h1>
        <p>Score: {{ score }}</p>
        <button class="restart-btn" @click="startGame">Play Again</button>
      </div>
    </div>

    <div class="game-header">
      <div class="score-board">
        <div class="score-item">
          <span class="label">Score</span>
          <span class="value">{{ score }}</span>
        </div>
        <div class="score-item">
          <span class="label">Lives</span>
          <span class="value">
            <span v-for="n in lives" :key="n" class="heart">❤️</span>
          </span>
        </div>
      </div>
      
      <div class="mission-display" :class="{ 'pulse': missionChanged }">
        <span class="mission-label">MISSION:</span>
        <span class="mission-target">Find {{ currentMission ? currentMission.setName : '...' }}</span>
      </div>
    </div>

    <div class="game-board" ref="board">
      <!-- Grid Background -->
      <div class="grid-background">
        <div v-for="i in gridSize * gridSize" :key="'bg-'+i" class="grid-cell"></div>
      </div>

      <!-- Snake -->
      <div 
        v-for="(segment, index) in snake" 
        :key="'snake-'+index"
        class="snake-segment"
        :class="{ 'snake-head': index === 0 }"
        :style="getSegmentStyle(segment)"
      >
        <div class="snake-inner"></div>
      </div>

      <!-- Food Items -->
      <div 
        v-for="item in boardItems" 
        :key="item.id"
        class="food-item"
        :style="getSegmentStyle(item)"
      >
        <img :src="getImgUrl(item.img)" :alt="item.word" class="food-img" />
        <div class="food-glow" :class="{ 'target-glow': isTarget(item) }"></div>
      </div>
    </div>

    <!-- Audio -->
    <audio ref="matchAudio"></audio>
    <audio ref="bgMusic" loop></audio>
  </div>
</template>

<script>
import gameData from '@/data/match3_data.json';

export default {
  name: 'SnakeGame',
  data() {
    return {
      gridSize: 20,
      snake: [],
      direction: { x: 1, y: 0 },
      nextDirection: { x: 1, y: 0 },
      boardItems: [],
      score: 0,
      lives: 3,
      isPlaying: false,
      gameOver: false,
      gameLoop: null,
      speed: 450,
      currentMission: null, // The category object
      missionChanged: false,
      allCategories: gameData,
      audioContext: null,
      baseLifetime: 20000, // 20 seconds initial lifetime
      lastUpdate: Date.now()
    };
  },
  computed: {
    // Filter categories to ensure they have items
    validCategories() {
      return this.allCategories.filter(c => c.items && c.items.length > 0);
    }
  },
  mounted() {
    // Focus the container for keyboard events
    this.$refs.gameContainer.focus();
  },
  beforeDestroy() {
    this.stopGame();
  },
  methods: {
    getImgUrl(path) {
      try {
        return require(`@/assets/${path}`);
      } catch (e) {
        return '';
      }
    },
    getSegmentStyle(pos) {
      return {
        left: `${pos.x * 5}%`,
        top: `${pos.y * 5}%`,
        width: '5%',
        height: '5%',
        opacity: pos.expiresAt ? Math.max(0.2, (pos.expiresAt - Date.now()) / pos.totalLifetime) : 1
      };
    },
    startGame() {
      this.snake = [
        { x: 10, y: 10 },
        { x: 9, y: 10 },
        { x: 8, y: 10 }
      ];
      this.direction = { x: 1, y: 0 };
      this.nextDirection = { x: 1, y: 0 };
      this.score = 0;
      this.lives = 3;
      this.isPlaying = true;
      this.gameOver = false;
      this.boardItems = [];
      this.speed = 450;
      this.baseLifetime = 20000;
      this.lastUpdate = Date.now();
      
      this.setNewMission();
      this.spawnItems(8);
      
      if (this.gameLoop) clearInterval(this.gameLoop);
      this.gameLoop = setInterval(this.update, this.speed);
      
      this.$refs.gameContainer.focus();
    },
    stopGame() {
      if (this.gameLoop) clearInterval(this.gameLoop);
      this.isPlaying = false;
    },
    setNewMission() {
      const randomCat = this.validCategories[Math.floor(Math.random() * this.validCategories.length)];
      this.currentMission = randomCat;
      this.missionChanged = true;
      setTimeout(() => { this.missionChanged = false; }, 500);
    },
    isTarget(item) {
      // Check if item belongs to current mission category
      // We need to check if the item ID exists in the current mission's items
      return this.currentMission.items.some(i => i.id === item.id);
    },
    spawnItems(count = 1) {
      // Ensure at least one target if none exist
      const hasTarget = this.boardItems.some(i => this.isTarget(i));
      let targetsToSpawn = hasTarget ? 0 : 1;
      let othersToSpawn = count - targetsToSpawn;
      
      // If count is large (initial), ensure mix
      if (count > 1 && !hasTarget) {
        targetsToSpawn = Math.max(1, Math.floor(count * 0.3)); // 30% targets
        othersToSpawn = count - targetsToSpawn;
      }

      for (let i = 0; i < targetsToSpawn; i++) {
        const item = this.getRandomItemFromCategory(this.currentMission);
        this.placeItemOnBoard(item);
      }
      
      for (let i = 0; i < othersToSpawn; i++) {
        const randomCat = this.validCategories[Math.floor(Math.random() * this.validCategories.length)];
        let item = this.getRandomItemFromCategory(randomCat);
        this.placeItemOnBoard(item);
      }
    },
    getRandomItemFromCategory(category) {
      const items = category.items;
      const randomItem = items[Math.floor(Math.random() * items.length)];
      return { ...randomItem }; // Clone
    },
    placeItemOnBoard(item) {
      let pos;
      let attempts = 0;
      do {
        pos = {
          x: Math.floor(Math.random() * this.gridSize),
          y: Math.floor(Math.random() * this.gridSize)
        };
        attempts++;
      } while (this.isOccupied(pos) && attempts < 100);
      
      if (attempts < 100) {
        const lifetime = this.baseLifetime;
        this.boardItems.push({ 
          ...item, 
          ...pos,
          expiresAt: Date.now() + lifetime,
          totalLifetime: lifetime
        });
      }
    },
    isOccupied(pos) {
      // Check snake
      if (this.snake.some(s => s.x === pos.x && s.y === pos.y)) return true;
      // Check other items
      if (this.boardItems.some(i => i.x === pos.x && i.y === pos.y)) return true;
      return false;
    },
    handleKeydown(e) {
      if (!this.isPlaying) return;
      
      switch(e.key) {
        case 'ArrowUp':
          if (this.direction.y !== 1) this.nextDirection = { x: 0, y: -1 };
          break;
        case 'ArrowDown':
          if (this.direction.y !== -1) this.nextDirection = { x: 0, y: 1 };
          break;
        case 'ArrowLeft':
          if (this.direction.x !== 1) this.nextDirection = { x: -1, y: 0 };
          break;
        case 'ArrowRight':
          if (this.direction.x !== -1) this.nextDirection = { x: 1, y: 0 };
          break;
      }
    },
    update() {
      const now = Date.now();
      this.lastUpdate = now;

      // Check item expiration
      this.boardItems = this.boardItems.filter(item => item.expiresAt > now);
      
      // Ensure minimum items and at least one target
      if (this.boardItems.length < 8) {
        this.spawnItems(8 - this.boardItems.length);
      }

      this.direction = this.nextDirection;
      
      const head = { ...this.snake[0] };
      head.x += this.direction.x;
      head.y += this.direction.y;
      
      // Wall Collision
      if (head.x < 0 || head.x >= this.gridSize || head.y < 0 || head.y >= this.gridSize) {
        this.handleCrash();
        return;
      }
      
      // Self Collision
      if (this.snake.some(s => s.x === head.x && s.y === head.y)) {
        this.handleCrash();
        return;
      }
      
      this.snake.unshift(head);
      
      // Check Item Collision
      const itemIndex = this.boardItems.findIndex(i => i.x === head.x && i.y === head.y);
      
      if (itemIndex !== -1) {
        const item = this.boardItems[itemIndex];
        if (this.isTarget(item)) {
          // Success
          this.score += 100;
          this.playAudio(item);
          this.setNewMission();
          
          // Remove eaten item
          this.boardItems.splice(itemIndex, 1);
          
          // Spawn 2 new items
          this.spawnItems(2);
          
          // Decrease lifetime for future items
          this.baseLifetime = Math.max(5000, this.baseLifetime - 500);
          
          // Increase speed (decrease interval)
          if (this.speed > 50) {
            this.speed = Math.max(50, this.speed - 25);
            clearInterval(this.gameLoop);
            this.gameLoop = setInterval(this.update, this.speed);
          }
          
          // Don't pop tail -> Grow
        } else {
          // Fail
          this.lives--;
          this.snake.pop(); // Move normally (don't grow)
          // Maybe shrink?
          if (this.snake.length > 1) this.snake.pop();
          
          this.boardItems.splice(itemIndex, 1); // Remove eaten item
          
          if (this.lives <= 0) {
            this.handleCrash();
            return;
          }
        }
      } else {
        this.snake.pop(); // Normal move
      }
    },
    handleCrash() {
      this.gameOver = true;
      this.stopGame();
    },
    playAudio(item) {
      if (!item || !item.mp3) return;
      // Default to English for now, or could add language selector
      const audioPath = item.mp3.en; 
      try {
        this.$refs.matchAudio.src = require(`@/assets/${audioPath}`);
        this.$refs.matchAudio.play().catch(e => console.log('Audio play error', e));
      } catch (e) {
        console.log('Audio not found', audioPath);
      }
    }
  }
};
</script>

<style scoped>
.snake-game-container {
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  background: #1a1a2e;
  border-radius: 20px;
  overflow: hidden;
  font-family: 'Outfit', sans-serif;
  outline: none;
  box-shadow: 0 20px 50px rgba(0,0,0,0.5);
}

.game-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: rgba(255,255,255,0.05);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.score-board {
  display: flex;
  gap: 20px;
}

.score-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.label {
  font-size: 12px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.value {
  font-size: 24px;
  font-weight: bold;
  color: #fff;
}

.mission-display {
  text-align: right;
  transition: transform 0.3s;
}

.mission-display.pulse {
  transform: scale(1.1);
}

.mission-label {
  display: block;
  font-size: 12px;
  color: #4ecca3;
  font-weight: bold;
}

.mission-target {
  font-size: 28px;
  font-weight: 800;
  background: linear-gradient(45deg, #4ecca3, #45a29e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.game-board {
  position: relative;
  width: 100%;
  padding-bottom: 100%; /* Aspect Ratio 1:1 */
  background: #0f0f1a;
}

.grid-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(20, 1fr);
  grid-template-rows: repeat(20, 1fr);
}

.grid-cell {
  border: 1px solid rgba(255,255,255,0.02);
}

.snake-segment {
  position: absolute;
  transition: all 0.1s linear;
  z-index: 10;
}

.snake-inner {
  width: 100%;
  height: 100%;
  background: #4ecca3;
  border-radius: 4px;
  box-shadow: 0 0 10px rgba(78, 204, 163, 0.5);
}

.snake-head .snake-inner {
  background: #fff;
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.8);
}

.food-item {
  position: absolute;
  z-index: 5;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s;
}

.food-img {
  width: 80%;
  height: 80%;
  object-fit: contain;
  z-index: 2;
}

.food-glow {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(0,0,0,0) 70%);
  z-index: 1;
}

.target-glow {
  background: radial-gradient(circle, rgba(255, 215, 0, 0.4) 0%, rgba(0,0,0,0) 70%);
  animation: pulse-glow 1.5s infinite;
}

@keyframes pulse-glow {
  0% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 0.8; }
  100% { transform: scale(1); opacity: 0.5; }
}

.game-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.8);
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(5px);
}

.start-screen, .game-over-screen {
  text-align: center;
  color: white;
}

h1 {
  font-size: 48px;
  margin-bottom: 20px;
  background: linear-gradient(to right, #4ecca3, #fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

button {
  padding: 15px 40px;
  font-size: 20px;
  background: #4ecca3;
  border: none;
  border-radius: 30px;
  color: #1a1a2e;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

button:hover {
  transform: scale(1.05);
  box-shadow: 0 0 20px rgba(78, 204, 163, 0.6);
}
</style>
